package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ClassKind
import top.wcpe.allincore.dsl.compiler.ast.DataField
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析顶层 `data Name { ... }` 或 `object Name { ... }` 声明块。
 *
 * 承接 data / object 语法，产出 [ClassDecl]（kind = [ClassKind.DATA] 或 [ClassKind.OBJECT]）；
 * Stage 6 扩展 fun，Stage 7 引入 object。
 *
 * 当前 token 必须是 [TokenType.KW_DATA] 或 [TokenType.KW_OBJECT]。
 * 返回时已消费 `}`（不消费后续 NEWLINE）。
 *
 * 类型名形式：
 *  - 推荐裸标识符：`data Item { ... }`（首字母大写），与使用侧 `: Item` 对称
 *  - 兼容旧字符串：`data "Item" { ... }` 仍可解析，但产生 [DslError.E792_CLASS_NAME_STRING_DEPRECATED]
 *    warning 提示迁移
 *
 * 校验规则：
 *  - 字段名形如 `[a-z][A-Za-z0-9_]*`（违反报 E759）
 *  - 字段类型必填；默认值可选
 *  - 同 data 内字段重名报 E757
 *
 * data / object 间同名（E765）由调用方在收集到列表后做去重校验。
 */
internal class ClassBlockParser(private val p: Parser) {

    companion object {
        /** `suspend` 是软关键字，词法层仍为 IDENTIFIER。 */
        private const val SOFT_KW_SUSPEND = "suspend"

        /** data 字段命名约束：必须以小写字母起始。 */
        private val FIELD_NAME_REGEX = Regex("[a-z][A-Za-z0-9_]*")

        /** 类型名命名约束：必须以大写字母起始（保持与使用侧 `: Item` 一致的可读性）。 */
        private val TYPE_NAME_REGEX = Regex("[A-Z][A-Za-z0-9_]*")
    }

    fun parse(): ClassDecl {
        // Stage 7a：同时接受 KW_DATA 与 KW_OBJECT，根据消费的 token 类型决定 ClassDecl.kind
        val cur = p.current()
        val kind = when (cur.type) {
            TokenType.KW_DATA -> ClassKind.DATA
            TokenType.KW_OBJECT -> ClassKind.OBJECT
            else -> p.fail("缺少 'data' 或 'object' 关键字", cur.position)
        }
        val kw = p.advance()
        val kindLabel = if (kind == ClassKind.DATA) "data" else "object"

        // 类型名兼容两种形式：
        //  - 标识符形式（推荐）：`data Item { ... }`，与使用侧 `: Item` 对称
        //  - 字符串形式（已废弃）：`data "Item" { ... }`，触发 E792 deprecation warning，仍可解析
        val nameTok = p.current()
        val typeName: String = when (nameTok.type) {
            TokenType.IDENTIFIER -> {
                if (!TYPE_NAME_REGEX.matches(nameTok.lexeme)) {
                    p.fail(DslError.E793_CLASS_NAME_INVALID, nameTok.position, kindLabel, nameTok.lexeme)
                }
                p.advance()
                nameTok.lexeme
            }
            TokenType.STRING -> {
                val literal = nameTok.literalValue as String
                p.warn(DslError.E792_CLASS_NAME_STRING_DEPRECATED, nameTok.position, kindLabel, literal)
                p.advance()
                literal
            }
            else -> p.fail(
                "$kindLabel 后必须是类型名标识符，例如 $kindLabel Item { ... }",
                nameTok.position,
            )
        }

        p.expect(TokenType.LBRACE, "$kindLabel 头部后缺少 '{'")
        p.skipNewlines()

        val fields = mutableListOf<DataField>()
        val funs = mutableListOf<FunDecl>()
        val nestedClasses = mutableListOf<ClassDecl>()
        val seenFields = linkedSetOf<String>()

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            when {
                // Stage 8：嵌套 class 声明（data / object）
                p.check(TokenType.KW_DATA) || p.check(TokenType.KW_OBJECT) -> {
                    nestedClasses += ClassBlockParser(p).parse()
                }
                // fun 声明
                p.check(TokenType.KW_FUN) -> {
                    funs += FunDeclParser(p).parseScreenLevel()
                }
                // suspend fun 声明（suspend 为软关键字）
                p.current().type == TokenType.IDENTIFIER
                    && p.current().lexeme == SOFT_KW_SUSPEND
                    && p.peek(1).type == TokenType.KW_FUN -> {
                    p.advance() // 消费 suspend
                    funs += FunDeclParser(p).parseScreenLevel(isSuspend = true)
                }
                // 字段声明
                else -> {
                    val field = parseField(typeName)
                    if (!seenFields.add(field.name)) {
                        p.fail(DslError.E757_DATA_DUPLICATE_FIELD, field.position, typeName, field.name)
                    }
                    fields += field
                }
            }
            // 成员分隔：允许 NEWLINE / ';'，并允许尾随
            while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) p.advance()
        }
        val close = p.expect(TokenType.RBRACE, "$kindLabel 块缺少 '}'")
        return ClassDecl(
            name = typeName,
            kind = kind,
            fields = fields,
            funs = funs,
            nestedClasses = nestedClasses,
            position = p.completePosition(kw.position, close),
        )
    }

    /** 解析单个字段 `name: Type [= default]`。 */
    private fun parseField(typeName: String): DataField {
        val nameTok = p.expect(TokenType.IDENTIFIER, "data 字段缺少字段名")
        if (!FIELD_NAME_REGEX.matches(nameTok.lexeme)) {
            p.fail(DslError.E759_DATA_FIELD_NAME_INVALID, nameTok.position, nameTok.lexeme)
        }
        p.expect(TokenType.COLON, "data 字段 '${nameTok.lexeme}' 后缺少 ':' 与类型标注")
        val type = TypeParser.parse(p)
        val default: ExprAst? = if (p.check(TokenType.EQUALS)) {
            p.advance()
            p.skipNewlines()
            ExpressionParser(p).parse()
        } else null
        return DataField(
            name = nameTok.lexeme,
            type = type,
            default = default,
            position = nameTok.position,
        )
    }

}
